import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  Stack,
  Box,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFeatures } from "../../../../../../../../context/PermissionsAndFeatures/FeaturesContext";

const PaymentDialog = ({
  open,
  onClose,
  payments,
  setPayments,
  banks,
  totalAmount,
  onSubmit,
  isDark,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // phone
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:1024px)"); // tablets / iPad
  const controlSize = isTablet ? "medium" : isXs ? "small" : "small";
  const dialogWidth = isXs ? "100vw" : isTablet ? "80vw" : "520px";

  // استخلاص حالة الميزة multi_payment و payment_methods
  const { hasFeature, featuresLoading } = useFeatures();
  const multiPaymentEnabled = !featuresLoading && hasFeature("multi_payment");
  const paymentMethodsEnabled = !featuresLoading && hasFeature("payment_methods");

  // سنعرض جميع الطرق دائماً، لكن سنعطّل الاختيار للطرق غير المسموح بها إذا كانت الميزة معطلة
  const allMethods = ["cash", "visa", "bank"];

  // ✅ useRef للتركيز على حقل المبلغ
  const amountInputRef = useRef(null);

  // ✅ useEffect لتحميل الخزنة المحفوظة من localStorage عند فتح الـ Dialog
  useEffect(() => {
    if (open && payments.length > 0) {
      const savedBankId = localStorage.getItem("selected_payment_bank");
      if (savedBankId && banks.length > 0) {
        // تحديث جميع صفوف الدفع بالخزنة المحفوظة
        const updated = payments.map((payment) => ({
          ...payment,
          bank_id: savedBankId,
        }));
        setPayments(updated);
      }
      
      // ✅ التركيز على حقل المبلغ بعد تحميل البيانات
      setTimeout(() => {
        if (amountInputRef.current) {
          amountInputRef.current.focus();
          amountInputRef.current.select();
        }
      }, 100);
    }
  }, [open, banks]);

  // ✅ useEffect لإضافة اختصار Enter لتأكيد الدفع
  useEffect(() => {
    const handleKeyDown = (e) => {
      // التحقق من مفتاح Enter
      if (e.key === 'Enter' && open) {
        e.preventDefault(); // منع السلوك الافتراضي
        onSubmit(); // تنفيذ تأكيد الدفع
      }
    };

    // إضافة المستمع فقط إذا كان الـ Dialog مفتوحاً
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }

    // تنظيف المستمع عند unmount أو إغلاق الـ Dialog
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onSubmit]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isXs} // full screen on small phones
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 4,
          bgcolor: isDark ? "#232334" : "#fff",
          color: isDark ? "#fff" : "#222",
          boxShadow: 8,
          p: 0,
          width: dialogWidth,
          maxWidth: "98vw",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: isDark ? "#232334" : "#fbc2eb",
          color: isDark ? "#fff" : "#232334",
          fontWeight: 700,
          fontSize: { xs: 18, sm: 20 },
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          pb: { xs: 1, sm: 1 },
          pt: { xs: 1.2, sm: 2 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span role="img" aria-label="pay" style={{ fontSize: 22 }}>
            💳
          </span>
          الدفع المتعدد
        </Box>
        <IconButton
          onClick={onClose}
          size={isTablet ? "medium" : "small"}
          sx={{ color: isDark ? "#fff" : "#232334" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          bgcolor: isDark ? "#232334" : "#fff",
          px: { xs: 1, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 1.5, sm: 1 },
        }}
      >
        <Typography
          sx={{ mb: 2, fontWeight: 500, color: "#888", textAlign: "center" }}
        >
          يمكنك تقسيم المبلغ على أكثر من طريقة دفع (كاش، فيزا، بنكي...)
        </Typography>
        {/* تعديل PaymentSection ليكون كل صف دفع أفقي */}
        <Stack spacing={2}>
          {payments.map((payment, idx) => (
            <Stack
              key={idx}
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems="center"
              sx={{
                bgcolor: isDark ? "#29293a" : "#f5f5f5",
                borderRadius: 2,
                p: { xs: 1, sm: 1 },
                boxShadow: 1,
              }}
            >
              {/* طريقة الدفع */}
              <TextField
                select
                label="طريقة الدفع"
                value={payment.payment_method}
                onChange={(e) => {
                  const updated = [...payments];
                  updated[idx].payment_method = e.target.value;
                  setPayments(updated);
                  localStorage.setItem(
                    "invoice_payments",
                    JSON.stringify(updated)
                  );
                }}
                sx={{ minWidth: { xs: "100%", sm: 130 }, flex: { xs: "1 1 auto", sm: "unset" } }}
                size={controlSize}
              >
                {allMethods.map((m) => (
                  <MenuItem
                    key={m}
                    value={m}
                    disabled={!paymentMethodsEnabled && m !== "cash"} // عند تعطيل الميزة: فقط "كاش" قابل للاختيار
                  >
                    {m === "cash" ? "كاش" : m === "visa" ? "فيزا" : "بنكي"}
                  </MenuItem>
                ))}
              </TextField>
              {/* الخزنة */}
              <TextField
                select
                label="الخزنة"
                value={payment.bank_id}
                onChange={(e) => {
                  const updated = [...payments];
                  updated[idx].bank_id = e.target.value;
                  setPayments(updated);
                  // ✅ حفظ الخزنة المختارة في localStorage
                  localStorage.setItem("selected_payment_bank", e.target.value);
                  localStorage.setItem(
                    "invoice_payments",
                    JSON.stringify(updated)
                  );
                }}
                sx={{ minWidth: { xs: "100%", sm: 130 }, flex: { xs: "1 1 auto", sm: "unset" } }}
                size={controlSize}
              >
                {banks.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id}>
                    {bank.name}
                  </MenuItem>
                ))}
              </TextField>
              {/* المبلغ */}
              <TextField
                inputRef={amountInputRef}
                label="المبلغ"
                type="number"
                value={payment.amount}
                onChange={(e) => {
                  const updated = [...payments];
                  updated[idx].amount = e.target.value;
                  setPayments(updated);
                  localStorage.setItem(
                    "invoice_payments",
                    JSON.stringify(updated)
                  );
                }}
                sx={{ minWidth: { xs: "100%", sm: 100 }, flex: { xs: "1 1 auto", sm: "unset" } }}
                size={controlSize}
                inputProps={{ min: 0 }}
                autoFocus={idx === 0}
              />
              {/* زر حذف صف الدفع إذا أكثر من واحد */}
              {payments.length > 1 && (
                <IconButton
                  color="error"
                  onClick={() => {
                    const updated = payments.filter((_, i) => i !== idx);
                    setPayments(updated);
                    localStorage.setItem(
                      "invoice_payments",
                      JSON.stringify(updated)
                    );
                  }}
                  size={isTablet ? "medium" : "small"}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
          {/* زر إضافة صف دفع جديد */}
          <Tooltip title={multiPaymentEnabled ? "" : "ميزة الدفع المتعدد (multi_payment) غير مفعّلة"}>
            <span>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  if (!multiPaymentEnabled) return;
                  setPayments([
                    ...payments,
                    { bank_id: "", amount: "", payment_method: "cash" },
                  ]);
                }}
                disabled={!multiPaymentEnabled}
                sx={{
                  alignSelf: { xs: "stretch", sm: "flex-end" },
                  mt: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 0.6 },
                }}
                size={controlSize}
              >
                إضافة طريقة دفع
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {/* إجمالي الفاتورة والمدفوع والمتبقي */}
        <Divider sx={{ my: 2, bgcolor: isDark ? "#444" : "#eee" }} />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={{ fontWeight: 700, color: "#888" }}>
              إجمالي الفاتورة:
            </Typography>
            <Typography
              sx={{ fontWeight: 700, color: "#f48fb1", fontSize: 18 }}
            >
              {totalAmount || 0}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={{ fontWeight: 700, color: "#1976d2" }}>
              المدفوع:
            </Typography>
            <Typography
              sx={{ fontWeight: 700, color: "#1976d2", fontSize: 18 }}
            >
              {payments
                .reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0)
                .toFixed(2)}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={{ fontWeight: 700, color: "#d32f2f" }}>
              المتبقي:
            </Typography>
            <Typography
              sx={{ fontWeight: 700, color: "#d32f2f", fontSize: 18 }}
            >
              {(
                parseFloat(totalAmount || 0) -
                payments.reduce(
                  (acc, p) => acc + (parseFloat(p.amount) || 0),
                  0
                )
              ).toFixed(2)}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          bgcolor: isDark ? "#232334" : "#fbc2eb",
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.2, sm: 2 },
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={onClose} color="secondary" sx={{ fontWeight: 600 }}>
          إلغاء
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          sx={{
            fontWeight: "bold",
            bgcolor: "#1976d2",
            color: "#fff",
            borderRadius: 2,
            px: { xs: 3, sm: 4 },
            ml: { xs: 1, sm: 2 },
            boxShadow: 2,
            fontSize: { xs: 15, sm: 17 },
            "&:hover": {
              bgcolor: "#1565c0",
            },
          }}
          title="تأكيد الدفع (Enter)" // ✅ إضافة tooltip يوضح الاختصار
          size={controlSize}
        >
          تأكيد الدفع
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;

